import React, { useMemo, useState } from "react";
import styles from "../styles/pages/EvaluatorPanel.module.css";
import {talk} from "../ALI_Ajax";
import Loading from "./LoadingBlock";


const CompletedTestViewer = ({ selectedTest }) => {
    if (!selectedTest) {
        return <div className={styles.emptyState}>Select a completed test to view details.</div>;
    }

    const [showRawJson, setShowRawJson] = useState(false);
    const [isLoadingFinalEvaluation, setIsLoadingFinalEvaluation] = useState(false);
    const [finalAIEvaluation, setFinalAIEvaluation] = useState(false);

    // Parse state_json safely
    const state = useMemo(() => {
        try {
            return selectedTest.state_json ? JSON.parse(selectedTest.state_json) : null;
        } catch (e) {
            console.error("Failed to parse state_json:", e);
            return null;
        }
    }, [selectedTest]);

    // Helpers
    const fmt = (dt) => {
        if (!dt) return "—";
        // Handle both "YYYY-MM-DD HH:MM:SS" and epoch numbers
        if (typeof dt === "number") return new Date(dt * 1000).toLocaleString();
        // Replace space with 'T' for cross-browser parsing
        const iso = dt.includes("T") ? dt : dt.replace(" ", "T");
        const d = new Date(iso);
        return isNaN(d.getTime()) ? dt : d.toLocaleString();
    };

    const badge = (text, type = "neutral") => (
        <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{text}</span>
    );

    const questions = state?.questions ?? [];

    const rubricRoot = state?.rubric?.rubric || null;
    const rubricSkills = state?.rubric?.skills || [];
    const overallLevel = state?.rubric?.overallLevel ?? rubricRoot?.estimated_overall_cefr ?? null;

    const downloadJson = () => {
        const blob = new Blob(
            [JSON.stringify(state ?? selectedTest, null, 2)],
            { type: "application/json" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fname = `test_${selectedTest.session_id || "unknown"}.json`;
        a.download = fname;
        a.click();
        URL.revokeObjectURL(url);
    };

    // const evaluateWritingTestAfterTheFact = () => {
    //     let testSessionId = selectedTest.session_id;
    //
    //     talk("ali_api/handlers/TestHandler.php",
    //         "EVALUATE_WRITING_TEST_AFTER_THE_FACT",
    //         {testSessionId: testSessionId},
    //         evaluateWritingTestAfterTheFactHandler
    //     );
    // }
    // const evaluateWritingTestAfterTheFactHandler = (operation, sentData, returnData) => {
    //     console.log(returnData);
    // }

    const generateOverallAIEvaluation = () => {
        setIsLoadingFinalEvaluation(true);

        let testSessionId = selectedTest.session_id;


        talk("ali_api/handlers/TestHandler.php",
            "GENERATE_OVERALL_AI_WRITING_EVALUATION",
            {testSessionId: testSessionId},
            generateOverallAIEvaluationHandler
        );
    }
    const generateOverallAIEvaluationHandler = (operation, sentData, returnData) => {
        setFinalAIEvaluation(returnData.data);
        setIsLoadingFinalEvaluation(false);
    }

    return (
        <div className={styles.detailWrap}>
            {/* Header / Summary */}
            <div className={styles.headerCard}>
                <div className={styles.headerTopRow}>
                    <div className={styles.sessionIdRow}>
                        <div className={styles.sessionIdLabel}>Session</div>
                        <div className={styles.sessionIdValue} title={selectedTest.session_id}>
                            {selectedTest.session_id}
                        </div>
                    </div>

                    <div className={styles.headerBadges}>
                        {badge(`Section: ${selectedTest.test_section || "—"}`, "info")}
                        {badge(`Examinee #${selectedTest.examinee_id || "?"}`, "neutral")}
                        {overallLevel ? badge(`Overall CEFR: ${overallLevel}`, "success") : badge("Overall CEFR: —", "neutral")}
                    </div>
                </div>

                <div className={styles.metaGrid}>
                    <div>
                        <div className={styles.metaLabel}>Started</div>
                        <div className={styles.metaValue}>{fmt(selectedTest.test_started_at)}</div>
                    </div>
                    <div>
                        <div className={styles.metaLabel}>Ended</div>
                        <div className={styles.metaValue}>{fmt(selectedTest.test_ended_at)}</div>
                    </div>
                    <div>
                        <div className={styles.metaLabel}>Last Saved</div>
                        <div className={styles.metaValue}>{fmt(selectedTest.last_saved_at)}</div>
                    </div>
                    <div>
                        <div className={styles.metaLabel}>State Saved At</div>
                        <div className={styles.metaValue}>{fmt(state?.last_saved_at)}</div>
                    </div>
                </div>

                <div className={styles.headerActions}>
                    <button className={styles.ghostButton} onClick={downloadJson}>Download JSON</button>
                    <button
                        className={styles.ghostButton}
                        onClick={() => setShowRawJson(v => !v)}
                        aria-expanded={showRawJson}
                    >
                        {showRawJson ? "Hide raw JSON" : "Show raw JSON"}
                    </button>
                </div>

                {showRawJson && (
                    <pre className={styles.rawJsonBlock}>
            {state ? JSON.stringify(state, null, 2) : selectedTest.state_json}
          </pre>
                )}
            </div>

            {/* Rubric */}
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>Rubric Overview</div>
                {rubricRoot ? (
                    <div className={styles.rubricGrid}>
                        {rubricSkills.map((skill) => {
                            const node = rubricRoot[skill];
                            if (!node) return null;
                            const subskills = node.subskills || {};
                            const subskillKeys = Array.isArray(subskills) ? [] : Object.keys(subskills);
                            const subskillCount = subskillKeys.length;
                            return (
                                <div key={skill} className={styles.rubricItem}>
                                    <div className={styles.rubricSkillRow}>
                                        <div className={styles.rubricSkillName}>{skill.replaceAll("_", " ")}</div>
                                        <div className={styles.rubricChips}>
                      <span className={styles.chip}>
                        Conf: {Number(node.confidence ?? 0)}
                      </span>
                                            <span className={styles.chip}>
                        CEFR: {node.estimated_cefr ?? "—"}
                      </span>
                                            <span className={styles.chip}>
                        Subskills: {subskillCount}
                      </span>
                                        </div>
                                    </div>
                                    <div className={styles.rubricSummary}>
                                        {node.summary ?? <span className={styles.muted}>(no summary yet)</span>}
                                    </div>
                                    {subskillCount > 0 && (
                                        <details className={styles.subskills}>
                                            <summary>View subskills</summary>
                                            <ul className={styles.subskillList}>
                                                {subskillKeys.map((k) => {
                                                    const s = subskills[k];
                                                    return (
                                                        <li key={k}>
                                                            <div className={styles.subskillRow}>
                                                                <span className={styles.subskillName}>{k.replaceAll("_", " ")}</span>
                                                                <span className={styles.subskillChips}>
                                  <span className={styles.chip}>Conf: {Number(s.confidence ?? 0)}</span>
                                  <span className={styles.chip}>CEFR: {s.estimated_cefr ?? "—"}</span>
                                  <span className={styles.chip}>Evidence: {(s.evidence?.length ?? 0)}</span>
                                </span>
                                                            </div>
                                                            <div className={styles.subskillSummary}>
                                                                {s.summary ?? <span className={styles.muted}>(no summary)</span>}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className={styles.muted}>No rubric data in this test yet.</div>
                )}
            </div>

            {/* Questions */}
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>Questions & Responses</div>
                {questions.length === 0 ? (
                    <div className={styles.muted}>No questions recorded.</div>
                ) : (
                    <ol className={styles.questionList}>
                        {questions.map((q) => (
                            <li key={q.questionNumber} className={styles.questionItem}>
                                <div className={styles.qHeader}>
                                    <div className={styles.qTitle}>
                                        <span className={styles.qNumber}>Q{q.questionNumber}</span>
                                        {q.isFollowUp && badge("Follow-up", "warning")}
                                        {q.isFinalQuestion && badge("Final", "success")}
                                        {q.isActive && badge("Active", "info")}
                                    </div>
                                    <div className={styles.qMeta}>
                                        <span className={styles.chip}>Allowed: {q.answer_time_allowed ?? "—"}s</span>
                                        <span className={styles.chip}>Asked: {fmt(q.questionCreatedTimeStamp)}</span>
                                        <span className={styles.chip}>Answered: {fmt(q.studentResponseTimeStamp)}</span>
                                    </div>
                                </div>

                                <div className={styles.qGrid}>
                                    <div className={styles.qCol}>
                                        <div className={styles.blockLabel}>Prompt</div>
                                        <div className={styles.blockText}>{q.question_text || <span className={styles.muted}>(no text)</span>}</div>
                                        {q.question_audio_url && (
                                            <div className={styles.audioRow}>
                                                <audio controls src={q.question_audio_url} className={styles.audioPlayer} />
                                            </div>
                                        )}
                                        {Array.isArray(q.target_skills) && q.target_skills.length > 0 && (
                                            <div className={styles.skillTags}>
                                                {q.target_skills.map((ts) => (
                                                    <span key={ts} className={styles.skillTag}>{ts.replaceAll("_", " ")}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.qCol}>
                                        <div className={styles.blockLabel}>Student Response</div>
                                        <div className={styles.blockText}>
                                            {q.student_response_text ?? <span className={styles.muted}>(no response)</span>}
                                        </div>
                                        {q.student_audio_url && (
                                            <div className={styles.audioRow}>
                                                <audio controls src={q.student_audio_url} className={styles.audioPlayer} />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.qCol}>
                                        <div className={styles.blockLabel}>AI Evaluation</div>
                                        <div className={styles.blockText}>
                                            {q.aiEvaluation ? <div>
                                                <div><b>Level</b> {q.aiEvaluation.cefr_level} ({q.aiEvaluation.modifier})</div>
                                                <div><b>Explanation</b> {q.aiEvaluation.explanation}</div>
                                                <div><b>Time for Eval</b> {q.aiEvaluation.timeToCompletion ?? ''}</div>
                                            </div> : '-no evaluation'}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>

            {/* EVALUATE BUTTON */}
            <div className={styles.sectionCard}>
                {isLoadingFinalEvaluation ? <Loading /> : <button
                    className={styles.ghostButton}
                    onClick={generateOverallAIEvaluation}
                >Generate Overall AI Evaluation</button>}

                {finalAIEvaluation && (
                    <div className={styles.sectionCard}>
                        <h2>AI Final Evaluation</h2>
                        <p className={styles.readableSummary}>
                            {finalAIEvaluation.readableSummary}
                        </p>

                        <h3>Overall Level</h3>
                        <p><strong>CEFR:</strong> {finalAIEvaluation.overallLevel.estimated_cefr}</p>
                        <p><strong>Confidence:</strong> {(finalAIEvaluation.overallLevel.confidence * 100).toFixed(0)}%</p>
                        <p>{finalAIEvaluation.overallLevel.summary}</p>

                        <h3>Skill Evaluations</h3>
                        <div className={styles.skillGrid}>
                            {Object.entries(finalAIEvaluation.evaluation).map(([skill, data]) => (
                                <div key={skill} className={styles.skillCard}>
                                    <h4 className={styles.skillTitle}>{skill.replace(/_/g, ' ')}</h4>
                                    <p><strong>CEFR:</strong> {data.estimated_cefr || '—'}</p>
                                    <p><strong>Confidence:</strong> {(data.confidence * 100).toFixed(0)}%</p>
                                    <p>{data.summary}</p>
                                </div>
                            ))}
                        </div>

                        <h3>Detailed Rubric</h3>
                        {Object.entries(finalAIEvaluation.updated_rubric).map(([category, catData]) => {
                            // skip the synthetic "estimated_overall_cefr"
                            if (category === 'estimated_overall_cefr') return null;
                            return (
                                <div key={category} className={styles.rubricSection}>
                                    <h4>{category.replace(/_/g, ' ')}</h4>
                                    {catData.estimated_cefr && (
                                        <p><strong>CEFR:</strong> {catData.estimated_cefr}</p>
                                    )}
                                    {catData.confidence > 0 && (
                                        <p><strong>Confidence:</strong> {(catData.confidence * 100).toFixed(0)}%</p>
                                    )}
                                    {catData.summary && <p>{catData.summary}</p>}

                                    {Array.isArray(catData.evidence) && catData.evidence.length > 0 && (
                                        <ul className={styles.evidenceList}>
                                            {catData.evidence.map((ev, i) => <li key={i}>{ev}</li>)}
                                        </ul>
                                    )}

                                    {catData.subskills && Object.keys(catData.subskills).length > 0 && (
                                        <div className={styles.subskillBlock}>
                                            <h5>Subskills</h5>
                                            {Object.entries(catData.subskills).map(([sub, subData]) => (
                                                <div key={sub} className={styles.subskillItem}>
                                                    <p><strong>{sub.replace(/_/g, ' ')}:</strong> {subData.estimated_cefr || '—'} ({(subData.confidence * 100).toFixed(0)}%)</p>
                                                    {subData.summary && <p>{subData.summary}</p>}
                                                    {Array.isArray(subData.evidence) && subData.evidence.length > 0 && (
                                                        <ul>
                                                            {subData.evidence.map((e, i) => <li key={i}>{e}</li>)}
                                                        </ul>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}


            </div>
        </div>
    );
}

export default CompletedTestViewer;